import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  RotateCcw,
  RotateCw,
  Maximize2,
  Minimize2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useTheme } from "@/context/ThemeContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import { cn } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";
import { auctionsLabels, commonLabels } from "@/i18n";

interface VideoPlayerProps {
  videoUrl: string;
  title: string;
  onClose: () => void;
  /** Start playing as soon as the video can play (default true) */
  autoplay?: boolean;
  /** Default playback speed (default 1). */
  defaultPlaybackRate?: number;
}

const VideoPlayer = ({
  videoUrl,
  title,
  onClose,
  autoplay = true,
  defaultPlaybackRate = 1,
}: VideoPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(defaultPlaybackRate);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handlersRef = useRef<{
    togglePlay: () => void;
    toggleFullscreen: () => void;
    showControlsTemporarily: () => void;
  }>(null!);
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const { language } = useLanguage();
  const auctionL = auctionsLabels(language);
  const commonL = commonLabels(language);

  // Prevent background scrolling when video player is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    if (isNative) {
      document.documentElement.classList.add("bilenia-native-video-open");
    }

    return () => {
      document.body.style.overflow = "unset";
      if (isNative) {
        document.documentElement.classList.remove("bilenia-native-video-open");
      }
    };
  }, [isNative]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => setCurrentTime(video.currentTime);
    const updateDuration = () => setDuration(video.duration);

    video.addEventListener("timeupdate", updateTime);
    video.addEventListener("loadedmetadata", updateDuration);

    return () => {
      video.removeEventListener("timeupdate", updateTime);
      video.removeEventListener("loadedmetadata", updateDuration);
    };
  }, []);

  // Autoplay when video is ready (user opened the modal, so play immediately)
  useEffect(() => {
    if (!autoplay) return;
    const video = videoRef.current;
    if (!video || !videoUrl) return;

    const tryPlay = () => {
      video
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => {
          /* Autoplay blocked by browser, user can click Play */
        });
    };

    if (video.readyState >= 2) {
      tryPlay();
    } else {
      video.addEventListener("canplay", tryPlay, { once: true });
      return () => video.removeEventListener("canplay", tryPlay);
    }
  }, [videoUrl, autoplay]);

  // Sync fullscreen state when user presses Esc or browser exits fullscreen
  useEffect(() => {
    const onFullscreenChange = () =>
      setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  // Keyboard: Space = play/pause, F = fullscreen
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === " ") {
        e.preventDefault();
        handlersRef.current?.togglePlay();
        handlersRef.current?.showControlsTemporarily();
      } else if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        handlersRef.current?.toggleFullscreen();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Auto-hide controls after 2 seconds
  useEffect(() => {
    if (showControls) {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2000);
    }

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [showControls]);

  const showControlsTemporarily = () => {
    setShowControls(true);
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.volume = volume;
    video.muted = isMuted;
    video.playbackRate = playbackRate;
  }, [volume, isMuted, playbackRate]);

  // Ensure initial playback rate is applied when opening / switching videos
  useEffect(() => {
    setPlaybackRate(defaultPlaybackRate);
  }, [defaultPlaybackRate, videoUrl]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
    setIsPlaying(!isPlaying);
    showControlsTemporarily();
  };

  const handleSeek = (value: number[]) => {
    const video = videoRef.current;
    if (!video) return;

    const newTime = (value[0] / 100) * duration;
    video.currentTime = newTime;
    setCurrentTime(newTime);
    showControlsTemporarily();
  };

  const skip = (seconds: number) => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = Math.max(
      0,
      Math.min(video.currentTime + seconds, duration),
    );
    showControlsTemporarily();
  };

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await containerRef.current.requestFullscreen();
      }
      showControlsTemporarily();
    } catch (err) {}
  };

  handlersRef.current = {
    togglePlay,
    toggleFullscreen,
    showControlsTemporarily,
  };

  // Single click = play/pause, double click = fullscreen (delay single click so double click cancels it)
  const handleVideoAreaClick = () => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
      return;
    }
    clickTimeoutRef.current = setTimeout(() => {
      clickTimeoutRef.current = null;
      togglePlay();
      showControlsTemporarily();
    }, 250);
  };

  const handleVideoAreaDoubleClick = () => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    toggleFullscreen();
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 2];

  const player = (
    <div
      className={cn(
        "fixed inset-0 flex justify-center overflow-y-auto native-video-player-layer",
        isNative
          ? "z-[270] items-center px-4 pb-4 pt-[max(2.75rem,env(safe-area-inset-top))]"
          : "z-[200] items-start p-4 pt-8 sm:pt-12",
      )}
      onClick={onClose}
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        aria-hidden
      />
      <div
        className="relative z-10 max-w-4xl w-full bg-black rounded-lg overflow-hidden shrink-0 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Video container: klick/dubbelklick här = play/pause + fullscreen (overlay ser till att klick når containern) */}
        <div
          ref={containerRef}
          className={`relative bg-black cursor-pointer select-none ${isFullscreen ? "flex items-center justify-center" : ""}`}
          onMouseEnter={!isMobile ? showControlsTemporarily : undefined}
          onMouseLeave={!isMobile ? () => setShowControls(false) : undefined}
          onClick={handleVideoAreaClick}
          onDoubleClick={handleVideoAreaDoubleClick}
        >
          <video
            ref={videoRef}
            className={`w-full h-auto pointer-events-none ${isFullscreen ? "max-h-[100vh] object-contain" : "max-h-[72vh] sm:max-h-[70vh]"}`}
            src={videoUrl}
            autoPlay
            playsInline
            onEnded={() => setIsPlaying(false)}
          />

          {/* Transparent overlay – fångar klick/touch så att de når containern (video har pointer-events-none) */}
          <div
            className="absolute inset-0 z-10"
            style={{ touchAction: "manipulation" }}
            aria-hidden
          />

          {/* Stäng-knapp i spelarens övre högra hörn */}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className={cn(
              "absolute right-2 h-9 w-9 p-0 rounded-full bg-black/50 hover:bg-black/70 text-white",
              isNative
                ? "z-[80] top-[max(0.5rem,env(safe-area-inset-top))]"
                : "z-30 top-2",
            )}
            aria-label={commonL.buttons?.close ?? "Stäng"}
          >
            <X className="h-5 w-5" />
          </Button>

          {/* Video Controls Overlay – higher z-index so controls stay on top and receive their own clicks */}
          <div
            className={`absolute bottom-0 left-0 right-0 z-20 p-2 sm:p-4 bg-gradient-to-t from-black/80 to-transparent transition-opacity duration-300 ${showControls ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            {/* Progress Bar */}
            <div className="mb-2 sm:mb-4">
              <Slider
                value={[duration > 0 ? (currentTime / duration) * 100 : 0]}
                onValueChange={handleSeek}
                max={100}
                step={0.1}
                className="w-full"
              />
              <div className="flex justify-between text-white text-xs sm:text-sm mt-1">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Control Buttons – wrap on mobile so they don't spill out */}
            <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-2">
              <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => skip(-10)}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0 sm:h-9 sm:w-9"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={togglePlay}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0 sm:h-9 sm:w-9"
                >
                  {isPlaying ? (
                    <Pause className="h-5 w-5" />
                  ) : (
                    <Play className="h-5 w-5" />
                  )}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => skip(10)}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0 sm:h-9 sm:w-9"
                >
                  <RotateCw className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-1 sm:gap-2 min-w-0">
                {/* Volume Control */}
                <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsMuted(!isMuted)}
                    className="text-white hover:bg-white/20 h-8 w-8 p-0 sm:h-9 sm:w-9"
                  >
                    {isMuted ? (
                      <VolumeX className="h-4 w-4" />
                    ) : (
                      <Volume2 className="h-4 w-4" />
                    )}
                  </Button>
                  <Slider
                    value={[isMuted ? 0 : volume * 100]}
                    onValueChange={(value) => {
                      setVolume(value[0] / 100);
                      setIsMuted(value[0] === 0);
                    }}
                    max={100}
                    className="w-14 sm:w-20 shrink-0"
                  />
                </div>

                {/* Speed Control */}
                <select
                  value={playbackRate}
                  onChange={(e) => setPlaybackRate(Number(e.target.value))}
                  className="border border-white/30 text-black rounded px-1.5 py-0.5 sm:px-2 sm:py-1 text-xs sm:text-sm shrink-0 min-w-0 max-w-[4rem]"
                >
                  {speedOptions.map((speed) => (
                    <option key={speed} value={speed}>
                      {speed}x
                    </option>
                  ))}
                </select>

                {/* Fullscreen */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleFullscreen}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0 sm:h-9 sm:w-9 shrink-0"
                  aria-label={
                    isFullscreen
                      ? ((auctionL as any).exitFullscreen ?? "Lämna helskärm")
                      : ((auctionL as any).fullscreen ?? "Helskärm")
                  }
                >
                  {isFullscreen ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isNative && typeof document !== "undefined") {
    return createPortal(player, document.body);
  }

  return player;
};

export default VideoPlayer;
